import { Component } from '@theme/component';
import {
  VariantSelectedEvent,
  VariantUpdateEvent,
} from '@theme/events';

import {
  morph,
  MORPH_OPTIONS,
} from '@theme/morph';

import {
  OverflowList,
} from '@theme/overflow-list';

import {
  yieldToMainThread,
  getViewParameterValue,
  ResizeNotifier,
} from '@theme/utilities';


/**
 * PICO&PALA VARIANT PICKER
 *
 * Comportamiento:
 *
 * - "Selecciona una opción" puede aparecer
 *   como placeholder visual.
 *
 * - NO se bloquea Agregar al carrito.
 *
 * - NO se elimina la variante inicial
 *   que Shopify ya tiene cargada.
 *
 * - Si el cliente no selecciona nada,
 *   Shopify puede utilizar esa variante inicial.
 *
 * - Cuando el cliente selecciona una variante,
 *   actualizamos normalmente producto,
 *   precio, stock e inventario.
 *
 * - El control de cantidad/stock continúa
 *   gestionándose desde quantity-selector
 *   y product-form.
 */

export default class VariantPicker extends Component {

  #pendingRequestUrl;

  #abortController;

  #checkedIndices = [];

  #radios = [];

  #resizeObserver =
    new ResizeNotifier(
      () =>
        this.updateVariantPickerCss()
    );


  connectedCallback() {

    super.connectedCallback();


    const fieldsets =
      this.refs.fieldsets || [];


    /*
     * Preparamos los radios.
     */

    fieldsets.forEach(
      (fieldset) => {

        const radios =
          Array.from(
            fieldset?.querySelectorAll(
              'input[type="radio"]'
            ) ?? []
          );


        this.#radios.push(
          radios
        );


        const checkedIndex =
          radios.findIndex(
            (radio) =>
              radio.checked === true
          );


        this.#checkedIndices.push(
          checkedIndex !== -1
            ? [checkedIndex]
            : []
        );

      }
    );


    this.addEventListener(
      'change',
      this.variantChanged.bind(this)
    );


    this.#resizeObserver.observe(
      this
    );

  }


  disconnectedCallback() {

    super.disconnectedCallback();

    this.#resizeObserver.disconnect();

  }


  /**
   * Comprueba si todas las opciones
   * necesarias están elegidas.
   *
   * IMPORTANTE:
   * si no están todas elegidas,
   * NO bloqueamos la compra.
   */

  hasCompleteSelection() {

    const fieldsets =
      Array.from(
        this.querySelectorAll(
          'fieldset.variant-option'
        )
      );


    const selects =
      Array.from(
        this.querySelectorAll(
          'select[data-pyp-variant-select]'
        )
      );


    const radiosComplete =
      fieldsets.every(
        (fieldset) =>
          Boolean(
            fieldset.querySelector(
              'input[type="radio"]:checked'
            )
          )
      );


    const selectsComplete =
      selects.every(
        (select) =>
          Boolean(
            select.value
          )
      );


    return (
      radiosComplete &&
      selectsComplete
    );

  }


  /**
   * Cambio de variante.
   */

  variantChanged(event) {

    if (
      !(
        event.target instanceof
        HTMLElement
      )
    ) {

      return;

    }


    const selectedOption =
      event.target instanceof
        HTMLSelectElement

        ? event.target.options[
            event.target.selectedIndex
          ]

        : event.target;


    if (!selectedOption) {

      return;

    }


    /*
     * "Selecciona una opción"
     * es simplemente un placeholder.
     *
     * No bloqueamos.
     * No vaciamos variantId.
     * No hacemos nada.
     */

    if (
      selectedOption.dataset
        ?.pypPlaceholder ===
        'true'
    ) {

      return;

    }


    this.updateSelectedOption(
      event.target
    );


    /*
     * Actualizamos el texto del swatch.
     */

    if (
      event.target instanceof
      HTMLInputElement
    ) {

      const fieldset =
        event.target.closest(
          'fieldset'
        );


      const display =
        fieldset?.querySelector(
          '[data-pyp-selected-value]'
        );


      if (display) {

        display.textContent =
          event.target.value;

      }

    }


    /*
     * Si el producto tiene varias opciones
     * y todavía falta alguna por escoger,
     * esperamos.
     *
     * Pero dejamos el botón totalmente
     * funcional con la variante inicial
     * que Shopify ya tiene cargada.
     */

    if (
      !this.hasCompleteSelection()
    ) {

      return;

    }


    /*
     * Ahora sí existe una selección completa.
     *
     * Informamos al product-form de que
     * empieza un cambio de variante.
     */

    this.dispatchEvent(
      new VariantSelectedEvent({
        id:
          selectedOption.dataset
            .optionValueId ?? '',
      })
    );


    const isOnProductPage =
      this.dataset
        .templateProductMatch ===
        'true' &&

      !event.target.closest(
        'product-card'
      ) &&

      !event.target.closest(
        'quick-add-dialog'
      );


    const currentUrl =
      this.dataset.productUrl
        ?.split('?')[0];


    const newUrl =
      selectedOption.dataset
        .connectedProductUrl;


    const loadsNewProduct =
      isOnProductPage &&
      Boolean(newUrl) &&
      newUrl !== currentUrl;


    const isOnFeaturedProductSection =
      Boolean(
        this.closest(
          'featured-product-information'
        )
      );


    const morphElementSelector =
      loadsNewProduct

        ? 'main'

        : isOnFeaturedProductSection

          ? 'featured-product-information'

          : undefined;


    /*
     * Pedimos a Shopify la variante
     * seleccionada.
     */

    this.fetchUpdatedSection(
      this.buildRequestUrl(
        selectedOption
      ),
      morphElementSelector
    );


    /*
     * Actualizamos la URL cuando Shopify
     * dispone de un variantId asociado.
     */

    const url =
      new URL(
        window.location.href
      );


    const variantId =
      selectedOption.dataset
        .variantId ||
      null;


    if (isOnProductPage) {

      if (variantId) {

        url.searchParams.set(
          'variant',
          variantId
        );

      } else {

        url.searchParams.delete(
          'variant'
        );

      }

    }


    if (loadsNewProduct) {

      url.pathname =
        newUrl;

    }


    if (
      url.href !==
      window.location.href
    ) {

      yieldToMainThread().then(
        () => {

          history.replaceState(
            {},
            '',
            url.toString()
          );

        }
      );

    }

  }


  #getFieldsetMeasurements(
    fieldsetIndex
  ) {

    const fieldsets =
      this.refs.fieldsets || [];


    const fieldset =
      fieldsets[
        fieldsetIndex
      ];


    const checkedIndices =
      this.#checkedIndices[
        fieldsetIndex
      ];


    const radios =
      this.#radios[
        fieldsetIndex
      ];


    if (
      !radios ||
      !checkedIndices ||
      !fieldset
    ) {

      return null;

    }


    const [
      currentIndex,
      previousIndex,
    ] =
      checkedIndices;


    return {

      fieldset,

      currentIndex,

      previousIndex,

      currentWidth:
        currentIndex !== undefined
          ? radios[
              currentIndex
            ]?.parentElement
              ?.offsetWidth
          : undefined,

      previousWidth:
        previousIndex !== undefined
          ? radios[
              previousIndex
            ]?.parentElement
              ?.offsetWidth
          : undefined,

    };

  }


  #applyFieldsetMeasurements({
    fieldset,
    currentWidth,
    previousWidth,
    currentIndex,
    previousIndex,
  }) {

    if (currentWidth) {

      fieldset.style.setProperty(
        '--pill-width-current',
        `${currentWidth}px`
      );

    } else if (
      currentIndex !== undefined
    ) {

      fieldset.style.removeProperty(
        '--pill-width-current'
      );

    }


    if (previousWidth) {

      fieldset.style.setProperty(
        '--pill-width-previous',
        `${previousWidth}px`
      );

    } else if (
      previousIndex !== undefined
    ) {

      fieldset.style.removeProperty(
        '--pill-width-previous'
      );

    }

  }


  updateFieldsetCss(
    fieldsetIndex
  ) {

    if (
      Number.isNaN(
        fieldsetIndex
      )
    ) {

      return;

    }


    const measurements =
      this.#getFieldsetMeasurements(
        fieldsetIndex
      );


    if (measurements) {

      this.#applyFieldsetMeasurements(
        measurements
      );

    }

  }


  /**
   * Actualiza la opción seleccionada.
   */

  updateSelectedOption(
    target
  ) {

    if (
      typeof target ===
      'string'
    ) {

      const targetElement =
        this.querySelector(
          `[data-option-value-id="${target}"]`
        );


      if (!targetElement) {

        throw new Error(
          'Target element not found'
        );

      }


      target =
        targetElement;

    }


    if (
      target instanceof
      HTMLInputElement
    ) {

      const fieldsetIndex =
        Number.parseInt(
          target.dataset
            .fieldsetIndex ||
          ''
        );


      const inputIndex =
        Number.parseInt(
          target.dataset
            .inputIndex ||
          ''
        );


      if (
        !Number.isNaN(
          fieldsetIndex
        ) &&
        !Number.isNaN(
          inputIndex
        )
      ) {

        const fieldsets =
          this.refs.fieldsets || [];


        const fieldset =
          fieldsets[
            fieldsetIndex
          ];


        const checkedIndices =
          this.#checkedIndices[
            fieldsetIndex
          ];


        const radios =
          this.#radios[
            fieldsetIndex
          ];


        if (
          radios &&
          checkedIndices &&
          fieldset
        ) {

          const [
            currentIndex,
            previousIndex,
          ] =
            checkedIndices;


          if (
            currentIndex !==
              undefined &&
            radios[
              currentIndex
            ]
          ) {

            radios[
              currentIndex
            ].dataset
              .previousChecked =
              'false';

          }


          if (
            previousIndex !==
              undefined &&
            radios[
              previousIndex
            ]
          ) {

            radios[
              previousIndex
            ].dataset
              .previousChecked =
              'false';

          }


          checkedIndices.unshift(
            inputIndex
          );


          checkedIndices.length =
            Math.min(
              checkedIndices.length,
              2
            );


          const newCurrentIndex =
            checkedIndices[0];


          const newPreviousIndex =
            checkedIndices[1];


          if (
            newCurrentIndex !==
              undefined &&
            radios[
              newCurrentIndex
            ]
          ) {

            radios[
              newCurrentIndex
            ].dataset
              .currentChecked =
              'true';

          }


          if (
            newPreviousIndex !==
              undefined &&
            radios[
              newPreviousIndex
            ]
          ) {

            radios[
              newPreviousIndex
            ].dataset
              .previousChecked =
              'true';


            radios[
              newPreviousIndex
            ].dataset
              .currentChecked =
              'false';

          }


          this.updateFieldsetCss(
            fieldsetIndex
          );

        }

      }


      target.checked =
        true;

    }


    if (
      target instanceof
      HTMLSelectElement
    ) {

      const newValue =
        target.value;


      const newSelectedOption =
        Array.from(
          target.options
        ).find(
          (option) =>
            option.value ===
            newValue
        );


      if (
        !newSelectedOption
      ) {

        return;

      }


      for (
        const option
        of target.options
      ) {

        option.removeAttribute(
          'selected'
        );

      }


      newSelectedOption.setAttribute(
        'selected',
        'selected'
      );

    }

  }


  /**
   * Construye la URL
   * para consultar a Shopify.
   */

  buildRequestUrl(
    selectedOption,
    source = null,
    sourceSelectedOptionsValues = []
  ) {

    let productUrl =
      selectedOption.dataset
        .connectedProductUrl ||
      this.#pendingRequestUrl ||
      this.dataset.productUrl;


    this.#pendingRequestUrl =
      productUrl;


    const params = [];


    const viewParamValue =
      getViewParameterValue();


    if (viewParamValue) {

      params.push(
        `view=${viewParamValue}`
      );

    }


    if (
      this.selectedOptionsValues
        .length &&
      !source
    ) {

      params.push(
        `option_values=${this.selectedOptionsValues.join(
          ','
        )}`
      );

    } else if (
      source ===
      'product-card'
    ) {

      if (
        this.selectedOptionsValues
          .length
      ) {

        params.push(
          `option_values=${sourceSelectedOptionsValues.join(
            ','
          )}`
        );

      } else {

        params.push(
          `option_values=${selectedOption.dataset.optionValueId}`
        );

      }

    }


    const SECTION_ID_MAP = {

      'quick-add-component':
        'section-rendering-product-card',

      'swatches-variant-picker-component':
        'section-rendering-product-card',

      'featured-product-information':
        this.closest(
          'featured-product-information'
        )?.id,

    };


    const closestSectionId =
      Object.keys(
        SECTION_ID_MAP
      ).find(
        (sectionId) =>
          this.closest(
            sectionId
          )
      );


    if (closestSectionId) {

      if (
        productUrl?.includes(
          '?'
        )
      ) {

        productUrl =
          productUrl.split(
            '?'
          )[0];

      }


      return (
        `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}` +
        `&${params.join('&')}`
      );

    }


    return (
      `${productUrl}?${params.join('&')}`
    );

  }


  /**
   * Pide a Shopify
   * la variante seleccionada.
   */

  fetchUpdatedSection(
    requestUrl,
    morphElementSelector
  ) {

    this.#abortController
      ?.abort();


    this.#abortController =
      new AbortController();


    fetch(
      requestUrl,
      {
        signal:
          this.#abortController
            .signal,
      }
    )

      .then(
        (response) =>
          response.text()
      )

      .then(
        (responseText) => {

          this.#pendingRequestUrl =
            undefined;


          const html =
            new DOMParser()
              .parseFromString(
                responseText,
                'text/html'
              );


          html
            .querySelector(
              'overflow-list[defer]'
            )
            ?.removeAttribute(
              'defer'
            );


          const textContent =
            html.querySelector(
              'variant-picker script[type="application/json"]'
            )?.textContent;


          if (!textContent) {

            return;

          }


          let variantData;


          try {

            variantData =
              JSON.parse(
                textContent
              );

          } catch (error) {

            console.error(
              'Unable to parse variant data',
              error
            );


            return;

          }


          let newProduct;


          if (
            morphElementSelector ===
            'main'
          ) {

            this.updateMain(
              html
            );

          } else if (
            morphElementSelector
          ) {

            this.updateElement(
              html,
              morphElementSelector
            );

          } else {

            const {
              overflowList,
            } =
              this.refs;


            const wasSwatchesExpanded =
              overflowList instanceof
                OverflowList &&
              overflowList.getAttribute(
                'disabled'
              ) === 'true';


            /*
             * Guardamos la selección real
             * antes de actualizar el HTML.
             *
             * Nuestro Liquid contiene el
             * placeholder "Selecciona una opción",
             * por eso tenemos que recordar
             * qué eligió el cliente.
             */

            const selectedValueIds =
              this.selectedOptionsValues;


            newProduct =
              this.updateVariantPicker(
                html
              );


            /*
             * Volvemos a aplicar visualmente
             * la opción escogida.
             */

            this.restoreSelectedOptions(
              selectedValueIds
            );


            if (
              wasSwatchesExpanded
            ) {

              const overflowListAfterMorph =
                this.refs.overflowList;


              if (
                overflowListAfterMorph instanceof
                OverflowList
              ) {

                overflowListAfterMorph.showAll();

              }

            }

          }


          /*
           * Shopify ha resuelto
           * una variante real.
           *
           * Informamos al product-form.
           *
           * NO tocamos directamente
           * el botón.
           */

          if (
            variantData &&
            variantData.id
          ) {

            this.dispatchEvent(
              new VariantUpdateEvent(
                variantData,
                String(
                  variantData.id
                ),
                {
                  html,

                  productId:
                    this.dataset
                      .productId ??
                    '',

                  newProduct,
                }
              )
            );

          }

        }
      )

      .catch(
        (error) => {

          if (
            error.name ===
            'AbortError'
          ) {

            console.warn(
              'Fetch aborted by user'
            );

          } else {

            console.error(
              error
            );

          }

        }
      );

  }


  /**
   * Recupera visualmente
   * las opciones elegidas después
   * de un morph de Shopify.
   */

  restoreSelectedOptions(
    optionValueIds = []
  ) {

    if (
      !Array.isArray(
        optionValueIds
      ) ||
      optionValueIds.length ===
        0
    ) {

      return;

    }


    for (
      const optionValueId
      of optionValueIds
    ) {

      /*
       * RADIO / BOTÓN
       */

      const radio =
        this.querySelector(
          `input[type="radio"][data-option-value-id="${optionValueId}"]`
        );


      if (
        radio instanceof
        HTMLInputElement
      ) {

        radio.checked =
          true;


        radio.dataset
          .currentChecked =
          'true';


        const fieldsetIndex =
          Number.parseInt(
            radio.dataset
              .fieldsetIndex ||
            ''
          );


        const inputIndex =
          Number.parseInt(
            radio.dataset
              .inputIndex ||
            ''
          );


        if (
          !Number.isNaN(
            fieldsetIndex
          ) &&
          !Number.isNaN(
            inputIndex
          )
        ) {

          this.#checkedIndices[
            fieldsetIndex
          ] =
            [inputIndex];


          this.updateFieldsetCss(
            fieldsetIndex
          );

        }


        const display =
          radio
            .closest(
              'fieldset'
            )
            ?.querySelector(
              '[data-pyp-selected-value]'
            );


        if (display) {

          display.textContent =
            radio.value;

        }


        continue;

      }


      /*
       * SELECT
       */

      const option =
        this.querySelector(
          `select option[data-option-value-id="${optionValueId}"]`
        );


      if (
        option instanceof
        HTMLOptionElement
      ) {

        const select =
          option.closest(
            'select'
          );


        if (
          select instanceof
          HTMLSelectElement
        ) {

          for (
            const item
            of select.options
          ) {

            item.removeAttribute(
              'selected'
            );

          }


          option.selected =
            true;


          option.setAttribute(
            'selected',
            'selected'
          );


          select.value =
            option.value;

        }

      }

    }

  }


  /**
   * Actualiza el variant-picker
   * utilizando el HTML de Shopify.
   */

  updateVariantPicker(
    newHtml
  ) {

    let newProduct;


    const newVariantPickerSource =
      newHtml.querySelector(
        this.tagName.toLowerCase()
      );


    if (
      !newVariantPickerSource
    ) {

      throw new Error(
        'No new variant picker source found'
      );

    }


    if (
      newVariantPickerSource instanceof
      HTMLElement
    ) {

      const newProductId =
        newVariantPickerSource
          .dataset.productId;


      const newProductUrl =
        newVariantPickerSource
          .dataset.productUrl;


      if (
        newProductId &&
        newProductUrl &&
        this.dataset.productId !==
          newProductId
      ) {

        newProduct = {

          id:
            newProductId,

          url:
            newProductUrl,

        };

      }


      this.dataset.productId =
        newProductId;


      this.dataset.productUrl =
        newProductUrl;

    }


    morph(
      this,
      newVariantPickerSource,
      {

        ...MORPH_OPTIONS,

        getNodeKey:
          (node) => {

            if (
              !(
                node instanceof
                HTMLElement
              )
            ) {

              return undefined;

            }


            return node.dataset.key;

          },

      }
    );


    /*
     * El DOM puede haber cambiado.
     * Reconstruimos los arrays internos.
     */

    this.rebuildRadioState();


    this.updateVariantPickerCss();


    return newProduct;

  }


  /**
   * Reconstruye el estado
   * interno de los botones/radios.
   */

  rebuildRadioState() {

    this.#radios =
      [];


    this.#checkedIndices =
      [];


    const fieldsets =
      this.refs.fieldsets || [];


    fieldsets.forEach(
      (fieldset) => {

        const radios =
          Array.from(
            fieldset?.querySelectorAll(
              'input[type="radio"]'
            ) ?? []
          );


        this.#radios.push(
          radios
        );


        const checkedIndex =
          radios.findIndex(
            (radio) =>
              radio.checked ===
              true
          );


        this.#checkedIndices.push(
          checkedIndex !== -1
            ? [checkedIndex]
            : []
        );

      }
    );

  }


  updateVariantPickerCss() {

    const fieldsets =
      this.refs.fieldsets || [];


    const measurements =
      fieldsets

        .map(
          (_, index) =>
            this.#getFieldsetMeasurements(
              index
            )
        )

        .filter(
          (measurement) =>
            measurement !==
            null
        );


    for (
      const measurement
      of measurements
    ) {

      this.#applyFieldsetMeasurements(
        measurement
      );

    }

  }


  /**
   * Actualiza un elemento completo
   * de la página.
   */

  updateElement(
    newHtml,
    elementSelector
  ) {

    const element =
      this.closest(
        elementSelector
      );


    const newElement =
      newHtml.querySelector(
        elementSelector
      );


    if (
      !element ||
      !newElement
    ) {

      throw new Error(
        `No new element source found for ${elementSelector}`
      );

    }


    morph(
      element,
      newElement
    );

  }


  /**
   * Actualiza el main
   * cuando cambia un producto
   * de listado combinado.
   */

  updateMain(
    newHtml
  ) {

    const main =
      document.querySelector(
        'main'
      );


    const newMain =
      newHtml.querySelector(
        'main'
      );


    if (
      !main ||
      !newMain
    ) {

      throw new Error(
        'No new main source found'
      );

    }


    morph(
      main,
      newMain
    );

  }


  /**
   * Última opción real seleccionada.
   *
   * Excluimos explícitamente
   * "Selecciona una opción".
   */

  get selectedOption() {

    const selectedOption =
      this.querySelector(
        'select option[selected]:not([data-pyp-placeholder]), fieldset input:checked'
      );


    if (
      !(
        selectedOption instanceof
          HTMLInputElement ||
        selectedOption instanceof
          HTMLOptionElement
      )
    ) {

      return undefined;

    }


    return selectedOption;

  }


  get selectedOptionId() {

    const {
      selectedOption,
    } =
      this;


    if (!selectedOption) {

      return undefined;

    }


    const {
      optionValueId,
    } =
      selectedOption.dataset;


    if (!optionValueId) {

      return undefined;

    }


    return optionValueId;

  }


  /**
   * Devuelve las opciones reales
   * elegidas por el cliente.
   *
   * El placeholder nunca se incluye.
   */

  get selectedOptionsValues() {

    const values =
      [];


    /*
     * Opciones que solo tienen
     * un único valor.
     */

    const singleOptions =
      this.querySelectorAll(
        '[data-pyp-single-option="true"][data-option-value-id]'
      );


    singleOptions.forEach(
      (option) => {

        const id =
          option.dataset
            .optionValueId;


        if (id) {

          values.push(
            id
          );

        }

      }
    );


    /*
     * Radios seleccionados.
     */

    const checkedRadios =
      this.querySelectorAll(
        'fieldset input[type="radio"]:checked'
      );


    checkedRadios.forEach(
      (radio) => {

        const id =
          radio.dataset
            .optionValueId;


        if (id) {

          values.push(
            id
          );

        }

      }
    );


    /*
     * Selects.
     */

    const selects =
      this.querySelectorAll(
        'select[data-pyp-variant-select]'
      );


    selects.forEach(
      (select) => {

        if (
          !select.value
        ) {

          return;

        }


        const option =
          select.options[
            select.selectedIndex
          ];


        if (
          option?.dataset
            ?.pypPlaceholder ===
            'true'
        ) {

          return;

        }


        const id =
          option?.dataset
            ?.optionValueId;


        if (id) {

          values.push(
            id
          );

        }

      }
    );


    /*
     * Evitamos duplicados.
     */

    return [
      ...new Set(
        values
      ),
    ];

  }

}


if (
  !customElements.get(
    'variant-picker'
  )
) {

  customElements.define(
    'variant-picker',
    VariantPicker
  );

}